// «Condiciones del plan» de una agencia: plan contratado, condiciones a
// medida (planOverrides), prueba de un plan superior y margen de ajuste. Es el
// mismo modelo que el editor del Admin de la app (views/admin.js ›
// showEditAgencyModal), con dos diferencias: no borra las condiciones que no
// enseña (cupo de alertas, funciones nuevas) y deja fijar el cupo de alertas
// de directo que ya lee el Worker. Formulario a la izquierda y, a la derecha,
// cómo queda el plan con lo que la agencia usa hoy.

import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Switch
} from '@nextui-org/react';
import { auth } from '../../lib/firebase';
import { AgencyShared as S } from '../../lib/agencyApp';
import {
  DAY_MS,
  INCLUDED_FEATURES,
  conditionsForm,
  featureFields,
  limitFields,
  planAlertQuota,
  planPatch,
  shortLabel
} from '../../lib/agencyAdmin';
import { saveAgencyPatch } from '../../lib/agencyActions';

const PACK_LABELS = { none: 'Sin pack', basic: 'Básico', standard: 'Standard', advanced: 'Avanzado' };
const HIGHLIGHT_LABELS = { none: 'Sin destacar', basic: 'Básico', mid: 'Medio', top: 'Superior' };

function fmtDate(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function limitText(n) {
  return S.isUnlimited(n) ? 'Ilimitado' : String(Number(n));
}

// Lo que el plan contratado da en cada campo, para enseñarlo al lado.
function planDefaults(planId) {
  const p = S.PLANS[planId] || S.PLANS.silver;
  return {
    limits: {
      maxCredits: p.maxCredits,
      maxProfiles: p.maxProfiles,
      maxManagers: p.maxManagers,
      maxAutoTickets: p.maxAutoTickets,
      maxAlerts: planAlertQuota(p.id)
    },
    features: p.features || {},
    animPack: p.animPack,
    highlightLevel: p.highlightLevel
  };
}

// Qué cambia entre el plan efectivo de ahora y el que quedaría.
function planChanges(before, after, beforeAlerts, afterAlerts) {
  const out = [];
  if (before.id !== after.id) out.push(`Plan ${before.baseLabel || before.label} → ${after.baseLabel || after.label}`);
  for (const { key, label } of limitFields(S)) {
    const a = key === 'maxAlerts' ? beforeAlerts : before[key];
    const b = key === 'maxAlerts' ? afterAlerts : after[key];
    if (Number(a) !== Number(b) && !(S.isUnlimited(a) && S.isUnlimited(b))) out.push(`${label}: ${limitText(a)} → ${limitText(b)}`);
  }
  for (const [key, k, f] of INCLUDED_FEATURES) {
    const a = !!(before.features || {})[key];
    const b = !!(after.features || {})[key];
    if (a !== b) out.push(`${shortLabel(S.t(k, f))}: ${b ? 'sí' : 'no'}`);
  }
  if (before.animPack !== after.animPack) out.push(`Pack de animaciones: ${PACK_LABELS[before.animPack] || before.animPack} → ${PACK_LABELS[after.animPack] || after.animPack}`);
  if (before.highlightLevel !== after.highlightLevel) out.push(`Destacado: ${HIGHLIGHT_LABELS[before.highlightLevel]} → ${HIGHLIGHT_LABELS[after.highlightLevel]}`);
  return out;
}

function alertsOf(effective, overrides) {
  const v = overrides?.limits?.maxAlerts;
  if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v);
  return planAlertQuota(effective.id);
}

function TriState({ value, onChange, planValue }) {
  const opts = [
    ['', `Plan (${planValue ? 'sí' : 'no'})`],
    ['yes', 'Sí'],
    ['no', 'No']
  ];
  return (
    <ButtonGroup size="sm" variant="flat">
      {opts.map(([v, label]) => (
        <Button
          key={v || 'plan'}
          color={value === v ? (v === 'no' ? 'danger' : v === 'yes' ? 'success' : 'primary') : 'default'}
          variant={value === v ? 'solid' : 'flat'}
          onPress={() => onChange(v)}
        >
          {label}
        </Button>
      ))}
    </ButtonGroup>
  );
}

function SectionTitle({ children, hint }) {
  return (
    <div className="mt-2 border-b border-default-200 pb-1">
      <p className="text-sm font-semibold">{children}</p>
      {hint && <p className="text-xs text-default-500">{hint}</p>}
    </div>
  );
}

export default function AgencyPlanEditor({ isOpen, onClose, agency, usage, usageReady = true, onSaved }) {
  const [form, setForm] = useState(null);
  const [askGrace, setAskGrace] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Se rellena al abrir (no con cada cambio del documento: perdería lo escrito).
  useEffect(() => {
    if (isOpen && agency) {
      setForm(conditionsForm(S, agency));
      setAskGrace(false);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const grantedBy = auth.currentUser?.email || '';
  const result = useMemo(() => {
    if (!form || !agency) return null;
    return planPatch(S, agency, form, { usage, grantedBy });
  }, [form, agency, usage, grantedBy]);

  if (!agency) return null;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setLimit = (key, v) => setForm((f) => ({ ...f, limits: { ...f.limits, [key]: v.replace(/[^0-9]/g, '') } }));
  const setFeature = (key, v) => setForm((f) => ({ ...f, features: { ...f.features, [key]: v } }));

  const defaults = planDefaults(form?.plan);
  const trialNow = S.agencyTrial(agency);
  const graceNow = S.agencyGrace(agency);
  const trialPlans = form ? (S.PLAN_ORDER || []).filter((id) => S.planRank(id) > S.planRank(form.plan)) : [];
  const effective = result?.effective;
  const overrides = result ? (result.set.planOverrides || null) : null;
  const beforeAlerts = alertsOf(result?.before || {}, agency.planOverrides);
  const afterAlerts = effective ? alertsOf(effective, overrides) : 0;
  const changes = result ? planChanges(result.before, effective, beforeAlerts, afterAlerts) : [];
  const trialChanged = result ? JSON.stringify(result.set.planTrial || null) !== JSON.stringify(agency.planTrial || null) : false;
  const hours = Number(result?.set.planGraceHours) || S.GRACE_DEFAULT_HOURS;

  async function save(withGrace) {
    if (!form || !usageReady) return;
    const patch = planPatch(S, agency, form, { usage, grantedBy, withGrace });
    if (patch.issues.length && !withGrace) {
      setAskGrace(true);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await saveAgencyPatch(agency.id, patch);
      onSaved?.(patch.issues.length
        ? `Plan guardado. La agencia tiene ${hours} h para ajustar lo que le sobra.`
        : 'Plan y condiciones guardados.');
      onClose();
    } catch (e) {
      setError(e.message || 'No se ha guardado.');
    } finally {
      setSaving(false);
    }
  }

  const usageRows = [
    ['maxCredits', 'Créditos asignados', usage?.creditsUsed],
    ['maxManagers', 'Managers', usage?.staffCount],
    ['maxProfiles', 'Perfiles LIVE', usage?.profilesCount]
  ];

  return (
    <Modal isOpen={isOpen} onClose={saving ? undefined : onClose} size="5xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-0.5">
          <span>Condiciones del plan</span>
          <span className="text-xs font-normal text-default-500">{agency.name || agency.id} · {agency.id}</span>
        </ModalHeader>
        <ModalBody>
          {form && (
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
              {/* ── Formulario ── */}
              <div className="flex flex-col gap-3">
                <SectionTitle hint="El precio sale siempre del plan contratado; las condiciones a medida no lo cambian.">Plan contratado</SectionTitle>
                <Select
                  aria-label="Plan contratado"
                  size="sm"
                  selectedKeys={[form.plan]}
                  onSelectionChange={(keys) => {
                    const plan = String(Array.from(keys)[0] || form.plan);
                    set({ plan, trialPlan: S.planRank(form.trialPlan) > S.planRank(plan) ? form.trialPlan : '' });
                  }}
                  className="max-w-xs"
                >
                  {(S.PLAN_ORDER || []).map((id) => (
                    <SelectItem key={id} textValue={S.PLANS[id].label}>
                      {S.PLANS[id].label} · {Number(S.PLANS[id].price).toLocaleString('es-ES')} $/mes
                    </SelectItem>
                  ))}
                </Select>

                <SectionTitle hint="Vacío = lo que da el plan. 9999 = ilimitado.">Límites a medida</SectionTitle>
                <div className="grid gap-3 sm:grid-cols-2">
                  {limitFields(S).map(({ key, label, worker }) => (
                    <Input
                      key={key}
                      size="sm"
                      label={label}
                      inputMode="numeric"
                      value={form.limits[key]}
                      onValueChange={(v) => setLimit(key, v)}
                      placeholder={`Plan: ${limitText(defaults.limits[key])}`}
                      description={worker ? 'Cuántos directos de sus creadores vigila TikControl a la vez.' : undefined}
                      isClearable
                      onClear={() => setLimit(key, '')}
                    />
                  ))}
                </div>

                <SectionTitle hint="«Plan» deja lo que dé el plan contratado (cambia solo si se cambia de plan).">Funciones</SectionTitle>
                <div className="flex flex-col gap-1.5">
                  {featureFields(S).map(({ key, label }) => (
                    <div key={key} className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm">{label}</span>
                      <TriState value={form.features[key]} onChange={(v) => setFeature(key, v)} planValue={!!defaults.features[key]} />
                    </div>
                  ))}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Select
                    size="sm"
                    label="Pack de animaciones"
                    selectedKeys={[form.animPack || 'plan']}
                    onSelectionChange={(keys) => {
                      const v = String(Array.from(keys)[0] || 'plan');
                      set({ animPack: v === 'plan' ? '' : v });
                    }}
                  >
                    {[['plan', `Plan (${PACK_LABELS[defaults.animPack] || 'Sin pack'})`], ...Object.keys(S.ANIM_PACKS).map((id) => [id, PACK_LABELS[id] || id])].map(([id, label]) => (
                      <SelectItem key={id}>{label}</SelectItem>
                    ))}
                  </Select>
                  <Select
                    size="sm"
                    label="Destacado en el buscador"
                    selectedKeys={[form.highlightLevel || 'plan']}
                    onSelectionChange={(keys) => {
                      const v = String(Array.from(keys)[0] || 'plan');
                      set({ highlightLevel: v === 'plan' ? '' : v });
                    }}
                  >
                    {[['plan', `Plan (${HIGHLIGHT_LABELS[defaults.highlightLevel] || 'Sin destacar'})`], ...(S.HIGHLIGHT_ORDER || Object.keys(S.HIGHLIGHT_LEVELS)).map((id) => [id, HIGHLIGHT_LABELS[id] || id])].map(([id, label]) => (
                      <SelectItem key={id}>{label}</SelectItem>
                    ))}
                  </Select>
                </div>

                <SectionTitle hint="Días de un plan superior. Al acabar vuelve sola a su plan contratado.">Prueba de plan</SectionTitle>
                <p className="text-xs text-default-500">
                  {trialNow
                    ? trialNow.active
                      ? `Ahora prueba ${S.PLANS[trialNow.plan]?.label} hasta el ${fmtDate(trialNow.endsAt)} (${trialNow.daysLeft} días)${trialNow.grantedBy ? ` · la dio ${trialNow.grantedBy}` : ''}.`
                      : `Su última prueba (${S.PLANS[trialNow.plan]?.label}) terminó el ${fmtDate(trialNow.endsAt)}.`
                    : 'No tiene ninguna prueba.'}
                </p>
                <Switch size="sm" isSelected={form.trialArmed} onValueChange={(v) => set({ trialArmed: v })}>
                  Dar, cambiar o quitar la prueba
                </Switch>
                {form.trialArmed && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Select
                      size="sm"
                      label="Plan de prueba"
                      selectedKeys={[form.trialPlan || 'none']}
                      onSelectionChange={(keys) => {
                        const v = String(Array.from(keys)[0] || 'none');
                        set({ trialPlan: v === 'none' ? '' : v });
                      }}
                    >
                      {[['none', 'Sin prueba (quitar la que tenga)'], ...trialPlans.map((id) => [id, S.PLANS[id].label])].map(([id, label]) => (
                        <SelectItem key={id}>{label}</SelectItem>
                      ))}
                    </Select>
                    <Input
                      size="sm"
                      label="Días"
                      inputMode="numeric"
                      value={form.trialDays}
                      isDisabled={!form.trialPlan}
                      onValueChange={(v) => set({ trialDays: v.replace(/[^0-9]/g, '') })}
                      description={form.trialPlan && trialNow?.active && trialNow.plan === form.trialPlan
                        ? 'Es la misma prueba: cuenta desde que empezó.'
                        : 'Empieza ahora.'}
                    />
                  </div>
                )}

                <SectionTitle hint="Si la agencia usa más de lo que incluye el plan nuevo, conserva lo que tiene estas horas para ajustarlo; después se le deshabilita lo que sobre.">Margen de ajuste</SectionTitle>
                <Input
                  size="sm"
                  label="Horas"
                  inputMode="numeric"
                  value={form.graceHours}
                  onValueChange={(v) => set({ graceHours: v.replace(/[^0-9]/g, '') })}
                  className="max-w-[160px]"
                />
                {graceNow?.active && (
                  <p className="text-xs text-warning-600">
                    Está en margen de ajuste (de {S.PLANS[graceNow.from]?.label} a {S.PLANS[graceNow.to]?.label || graceNow.to}) hasta el {fmtDate(graceNow.until)}.
                  </p>
                )}
              </div>

              {/* ── Cómo queda ── */}
              <aside className="flex flex-col gap-3 lg:sticky lg:top-0 lg:self-start">
                <div className="border-b-2 border-primary pb-1">
                  <p className="text-xs uppercase tracking-wide text-default-500">Así queda</p>
                  <p className="text-lg font-semibold">{effective?.label}</p>
                  {effective?.trial && (
                    <p className="text-xs text-primary">Prueba de {effective.label} · {effective.trial.daysLeft} días (vuelve a {S.PLANS[form.plan]?.label})</p>
                  )}
                </div>

                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-default-500">
                      <th className="py-1 text-left font-normal" />
                      <th className="py-1 text-right font-normal">En uso</th>
                      <th className="py-1 text-right font-normal">Tope</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageRows.map(([key, label, used]) => {
                      const max = effective?.[key];
                      const over = !S.isUnlimited(max) && Number(used) > Number(max);
                      return (
                        <tr key={key} className={over ? 'text-danger-500' : ''}>
                          <td className="py-0.5">{label}</td>
                          <td className="py-0.5 text-right tabular-nums">{used == null ? '…' : used}</td>
                          <td className="py-0.5 text-right tabular-nums">{limitText(max)}</td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td className="py-0.5">Tickets automáticos / mes</td>
                      <td className="py-0.5 text-right">—</td>
                      <td className="py-0.5 text-right tabular-nums">{limitText(effective?.maxAutoTickets)}</td>
                    </tr>
                    <tr>
                      <td className="py-0.5">Directos vigilados</td>
                      <td className="py-0.5 text-right">—</td>
                      <td className="py-0.5 text-right tabular-nums">{afterAlerts}</td>
                    </tr>
                  </tbody>
                </table>

                <div className="flex flex-col gap-0.5">
                  {INCLUDED_FEATURES.map(([key, k, f]) => {
                    const on = !!(effective?.features || {})[key];
                    const custom = typeof overrides?.features?.[key] === 'boolean' && overrides.features[key] !== !!defaults.features[key];
                    return (
                      <div key={key} className={`flex items-center gap-1.5 text-xs ${on ? '' : 'text-default-400'}`}>
                        <span className={on ? 'text-success-500' : 'text-default-400'}>{on ? '✓' : '✕'}</span>
                        <span>{shortLabel(S.t(k, f))}</span>
                        {custom && <Chip size="sm" variant="flat" color="secondary" className="h-4 text-[10px]">a medida</Chip>}
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-default-200 pt-2 text-xs">
                  <p className="mb-1 font-semibold">Cambios al guardar</p>
                  {changes.length || trialChanged ? (
                    <ul className="list-disc pl-4 text-default-600">
                      {changes.map((c) => <li key={c}>{c}</li>)}
                      {trialChanged && (
                        <li>
                          {result.set.planTrial
                            ? `Prueba de ${S.PLANS[result.set.planTrial.plan]?.label}: ${result.set.planTrial.days} días, hasta el ${fmtDate(result.set.planTrial.endsAt)}`
                            : 'Se quita la prueba'}
                        </li>
                      )}
                    </ul>
                  ) : (
                    <p className="text-default-500">Nada cambia en lo que la agencia tiene hoy.</p>
                  )}
                </div>

                {result?.issues.length > 0 && (
                  <div className="border-l-2 border-warning pl-2 text-xs">
                    <p className="font-semibold text-warning-600">Usa más de lo que incluye</p>
                    <ul className="mt-1 flex flex-col gap-1">
                      {result.issues.map((i) => (
                        <li key={i.key}>
                          <span className="font-medium">{i.label}</span>
                          {i.max ? `: ${i.used} / ${limitText(i.max)}` : `: ${i.used} en uso`}
                          <span className="block text-default-500">{i.hint}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-default-500">Al guardar tendrá {hours} h para ajustarlo.</p>
                  </div>
                )}
              </aside>
            </div>
          )}

          {askGrace && result?.issues.length > 0 && (
            <div className="rounded-medium bg-warning-50 p-3 text-sm text-warning-700">
              La agencia usa más de lo que incluye el plan nuevo. Conservará lo que tiene durante {hours} h para
              ajustarlo (el dueño lo ve en su consola) y después se le deshabilitará lo que sobre. ¿Guardar así?
            </div>
          )}
          {error && <p className="text-sm text-danger-500">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={saving}>Cancelar</Button>
          {askGrace ? (
            <Button color="warning" onPress={() => save(true)} isLoading={saving}>
              Guardar con {hours} h de margen
            </Button>
          ) : (
            <Button color="primary" onPress={() => save(false)} isLoading={saving} isDisabled={!form || !usageReady}>
              {usageReady ? 'Guardar' : 'Cargando lo que usa…'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export { DAY_MS };
